import { Button, CircularProgress, Divider, FormControlLabel, FormGroup, Switch, TextField, Typography, Icon, IconButton, Checkbox, FormControl, InputLabel, MenuItem, Select, Paper } from '@material-ui/core'
import { useValues } from 'hooks'
import { createUserService, editUserService, FIELDS, EDIT_PATH } from 'lib'
import React from 'react'
import { useLocation, useHistory } from 'react-router'
import { IUser } from 'types'
import { IdentificationInput } from 'components'

interface Props {
    onSuccess: () => void
    onError: () => void
    onClose: () => void
}

const UserForm = (props: Props) => {
    const location = useLocation()
    const history = useHistory()
    const { onError, onSuccess, onClose } = props
    const initialInput = React.useRef<any>(null)
    const [loading, setLoading] = React.useState<boolean>(false)
    const { values: user, updateValue: handleChange, updateValues } = useValues()
    const handleSumbit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            setLoading(true)

            // Validación: al menos un nivel de creación
            if (!user.nivelCreacion?.empleado && !user.nivelCreacion?.cajaChica) {
                alert("Debes seleccionar al menos una opción de nivel de creación")
                return
            }

            // Convertimos nivelCreacion a un int
            let nivelCreacionInt = 0
            if (user.nivelCreacion?.empleado && user.nivelCreacion?.cajaChica) {
                nivelCreacionInt = 3
            } else if (user.nivelCreacion?.empleado) {
                nivelCreacionInt = 1
            } else if (user.nivelCreacion?.cajaChica) {
                nivelCreacionInt = 2
            }

            const payload = {
                ...user,
                nivelCreacion: nivelCreacionInt,
            }


            if (!(location.state as any)?.edit) {
                await createUserService({ ...payload, active: true })
            } else {
                const edited = { ...payload }
                delete edited[FIELDS.userId.key]
                await editUserService(user[FIELDS.userId.key], { ...edited })
            }
            setLoading(false)
            if (!(location.state as any)?.edit) {
                updateValues({
                    name: "", email: "", password: "", identification: "",
                    validUntil: new Date(), active: true
                } as IUser)
                initialInput.current?.focus()
            }
            onSuccess()
        } catch (error) {
            setLoading(false)
            onError()
        }
    }
    React.useEffect(() => {
        if ((location.state as any)?.edit) {
            if ((location.state as any)?.values) {
                const editValues = { ...(location.state as any)?.values }

                // Mapear rolReembolso a rol para el Select
                editValues.rol = editValues.rolReembolso || ""

                // Convertir nivelCreacion int a objeto para los checkboxes
                const nivel = editValues.nivelCreacion
                editValues.nivelCreacion = {
                    empleado: nivel === 1 || nivel === 3,
                    cajaChica: nivel === 2 || nivel === 3
                }

                updateValues(editValues)
            }
        } else {
            history.replace(location.pathname.replace(EDIT_PATH, ""))
        }
    }, [location.state, updateValues, history, location.pathname])

    return (
        <div className="p-4">
            <div className="flex items-center justify-between">
                <Typography variant="body2" style={{ fontWeight: 600, letterSpacing: 0.5 }}>
                    {(location.state as any)?.edit ? "Editar Usuario" : "Nuevo Usuario"}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <Icon fontSize="small">close</Icon>
                </IconButton>
            </div>
            <form onSubmit={handleSumbit} className="pt-4 flex flex-col" style={{ width: 300 }}>

                <FormControl variant="outlined" size="small" fullWidth style={{ marginTop: 10, marginBottom: 20 }}>
                    <InputLabel id="rol-label">Rol</InputLabel>
                    <Select
                        labelId="rol-label"
                        value={user['rol'] || ""}
                        onChange={(e) => handleChange('rol', e.target.value)}
                        label="Rol"
                        required
                    >
                        <MenuItem value="Empleado">Empleado</MenuItem>
                        <MenuItem value="Supervisor">Supervisor</MenuItem>
                        <MenuItem value="Coordinación Contable">Coordinación Contable</MenuItem>
                        <MenuItem value="Contador General">Contador General</MenuItem>
                        <MenuItem value="Recursos Humanos">Recursos Humanos</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    inputRef={initialInput}
                    autoFocus
                    size="small"
                    label={FIELDS.name.name}
                    variant="outlined"
                    fullWidth
                    required
                    value={user[FIELDS.name.key]}
                    onChange={(e) => handleChange(FIELDS.name.key, e.target.value)}
                />
                <TextField
                    size="small"
                    label={FIELDS.email.name}
                    variant="outlined"
                    fullWidth
                    style={{ marginTop: 10 }}
                    required
                    value={user[FIELDS.email.key]}
                    onChange={(e) => handleChange(FIELDS.email.key, e.target.value)}
                />
                {
                    !(location.state as any)?.edit &&
                    <TextField
                        size="small"
                        label={FIELDS.password.name}
                        variant="outlined"
                        type="password"
                        fullWidth
                        style={{ marginTop: 10 }}
                        required
                        value={user[FIELDS.password.key]}
                        onChange={(e) => handleChange(FIELDS.password.key, e.target.value)}
                    />
                }
                <TextField
                    size="small"
                    label={FIELDS.identification.name}
                    InputProps={{
                        inputComponent: IdentificationInput as any,
                    }}
                    variant="outlined"
                    fullWidth
                    style={{ marginTop: 10 }}
                    value={user[FIELDS.identification.key]}
                    onChange={(e) => handleChange(FIELDS.identification.key, e.target.value)}
                />

                <Paper
                    variant="outlined"
                    style={{
                        padding: 12,
                        marginTop: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        backgroundColor: '#f9f9f9',
                        borderColor: '#ccc'
                    }}
                >
                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                        Nivel de creación
                    </Typography>
                    <FormGroup row>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(user.nivelCreacion?.empleado)}
                                    onChange={(e) =>
                                        handleChange("nivelCreacion", { ...user.nivelCreacion, empleado: e.target.checked })
                                    }
                                    name="empleado"
                                />
                            }
                            label="Reembolso de empleados"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(user.nivelCreacion?.cajaChica)}
                                    onChange={(e) =>
                                        handleChange("nivelCreacion", { ...user.nivelCreacion, cajaChica: e.target.checked })
                                    }
                                    name="cajaChica"
                                />
                            }
                            label="Reposición de caja chica"
                        />
                    </FormGroup>
                </Paper>

                <div className="mt-4">
                    <Divider />
                </div>
                <div className="flex justify-end pt-3">
                    <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        disableElevation
                        disabled={loading}
                        type="submit"
                    >
                        {loading ? <CircularProgress style={{ color: "white" }} size={16} className="my-1" thickness={10} /> : (location.state as any)?.edit ? "Guardar" : "Registrar"}
                    </Button>
                </div>
            </form>
        </div>
    )
}

export default UserForm